// Service de gestion des sauvegardes de configuration tenant
import { supabase } from '../lib/supabase';

export interface ConfigBackup {
  id: string;
  tenant_id: number;
  domain_id: number;
  domain_name: string;
  backup_type: 'manual' | 'automatic';
  backup_status: 'pending' | 'uploading' | 'completed' | 'failed';
  google_drive_file_id?: string;
  google_drive_file_url?: string;
  file_size?: number;
  error_message?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  total_count?: number;
}

export interface BackupListResponse {
  backups: ConfigBackup[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Récupère la liste des sauvegardes avec pagination
 */
export async function getConfigBackups(
  tenantId: number,
  page: number = 1,
  pageSize: number = 20,
  backupType?: 'manual' | 'automatic'
): Promise<BackupListResponse> {
  try {
    const offset = (page - 1) * pageSize;

    const { data, error } = await supabase.rpc('get_config_backups', {
      tenant_id_param: tenantId,
      limit_param: pageSize,
      offset_param: offset,
      backup_type_param: backupType || null,
    });

    if (error) throw error;

    const backups = data || [];
    const total = backups.length > 0 ? Number(backups[0].total_count) : 0;
    const totalPages = Math.ceil(total / pageSize);

    return {
      backups,
      total,
      page,
      pageSize,
      totalPages,
    };
  } catch (error) {
    console.error('Error fetching config backups:', error);
    throw error;
  }
}

/**
 * Crée une sauvegarde manuelle (métadonnées seulement, contenu sur Google Drive)
 */
export async function createManualBackup(
  tenantId: number,
  domainId: number,
  domainName: string
): Promise<string> {
  try {
    // Récupérer l'utilisateur courant
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error('Utilisateur non authentifié');
    }

    // Créer l'enregistrement de sauvegarde via RPC (sans config_data)
    const { data: backupId, error } = await supabase.rpc('create_config_backup', {
      tenant_id_param: tenantId,
      domain_id_param: domainId,
      domain_name_param: domainName,
      created_by_param: user.id,
    });

    if (error) throw error;
    if (!backupId) throw new Error('Échec de la création de la sauvegarde');

    return backupId;
  } catch (error) {
    console.error('Error creating manual backup:', error);
    throw error;
  }
}

/**
 * Upload une sauvegarde vers Google Drive
 */
export async function uploadBackupToGoogleDrive(
  backupId: string,
  tenantId: number,
  domainId: number,
  domainName: string,
  configData: any,
  googleDriveFolderId?: string
): Promise<{ success: boolean; fileId?: string; fileUrl?: string; error?: string }> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Session invalide');
    }

    const response = await supabase.functions.invoke('backup-tenant-config', {
      body: {
        backupId,
        tenantId,
        domainId,
        domainName,
        configData,
        googleDriveFolderId,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error) {
      throw response.error;
    }

    return {
      success: response.data.success,
      fileId: response.data.google_drive_file_id,
      fileUrl: response.data.google_drive_file_url,
      error: response.data.error,
    };
  } catch (error) {
    console.error('Error uploading backup to Google Drive:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue',
    };
  }
}

/**
 * Crée une sauvegarde complète (création + upload Google Drive)
 */
export async function createAndUploadBackup(
  tenantId: number,
  domainId: number,
  domainName: string,
  configData: any,
  googleDriveFolderId?: string
): Promise<{ success: boolean; backupId?: string; error?: string }> {
  try {
    // 1. Créer l'enregistrement de sauvegarde (métadonnées seulement)
    const backupId = await createManualBackup(tenantId, domainId, domainName);

    // 2. Uploader vers Google Drive
    const uploadResult = await uploadBackupToGoogleDrive(
      backupId,
      tenantId,
      domainId,
      domainName,
      configData,
      googleDriveFolderId
    );

    if (!uploadResult.success) {
      return {
        success: false,
        backupId,
        error: uploadResult.error || 'Échec de l\'upload vers Google Drive',
      };
    }

    return {
      success: true,
      backupId,
    };
  } catch (error) {
    console.error('Error creating and uploading backup:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue',
    };
  }
}

/**
 * Restaure une sauvegarde depuis Google Drive
 */
export async function restoreBackup(backupId: string): Promise<{
  success: boolean;
  domainId?: number;
  domainName?: string;
  error?: string;
}> {
  try {
    // 1. Récupérer les métadonnées de la sauvegarde
    const { data: backupInfo, error: rpcError } = await supabase.rpc('restore_config_backup', {
      backup_id_param: backupId,
    });

    if (rpcError) throw rpcError;

    // Vérifier si la fonction a retourné un tableau
    const info = Array.isArray(backupInfo) ? backupInfo[0] : backupInfo;

    if (!info || !info.success) {
      throw new Error(info?.error || 'Échec de la récupération des informations de sauvegarde');
    }

    // 2. Télécharger et restaurer depuis Google Drive via Edge Function
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Session invalide');
    }

    const response = await supabase.functions.invoke('restore-tenant-config', {
      body: {
        backupId,
        googleDriveFileId: info.google_drive_file_id,
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error) {
      throw response.error;
    }

    if (!response.data.success) {
      throw new Error(response.data.error || 'Échec de la restauration');
    }

    return {
      success: true,
      domainId: response.data.domain_id,
      domainName: response.data.domain_name,
    };
  } catch (error) {
    console.error('Error restoring backup:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue',
    };
  }
}

/**
 * Supprime une sauvegarde
 */
export async function deleteBackup(backupId: string): Promise<boolean> {
  try {
    const { error } = await supabase.from('config_backups').delete().eq('id', backupId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting backup:', error);
    throw error;
  }
}

/**
 * Récupère une sauvegarde spécifique
 */
export async function getBackupById(backupId: string): Promise<ConfigBackup | null> {
  try {
    const { data, error } = await supabase
      .from('config_backups')
      .select('*')
      .eq('id', backupId)
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error fetching backup:', error);
    return null;
  }
}

/**
 * Met à jour le statut d'une sauvegarde
 */
export async function updateBackupStatus(
  backupId: string,
  status: 'pending' | 'uploading' | 'completed' | 'failed',
  errorMessage?: string
): Promise<boolean> {
  try {
    const updateData: any = {
      backup_status: status,
      updated_at: new Date().toISOString(),
    };

    if (errorMessage) {
      updateData.error_message = errorMessage;
    }

    const { error } = await supabase.from('config_backups').update(updateData).eq('id', backupId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error updating backup status:', error);
    throw error;
  }
}
